import axios, { AxiosError } from "axios";
import { useCallback, useEffect, useState } from "react";

type Data<T> = {
  data: T | null
  error: boolean
  isLoading: boolean
}

export function useAxios<T>(url: string | null, initialState?: T, debounce?: number) {
  const [data, setData] = useState<Data<T>>({
    data: initialState || null,
    error: false,
    isLoading: false
  })

  const fetchData = useCallback(async (url: string, controller: AbortController) => {
    setData((_data) => ({ ..._data, isLoading: true }))

    try {
      const { data } = await axios({
        method: 'GET', // This hooks serves only for data fetching, no other http methods
        url,
        headers: {
          authorization: `Bearer ${process.env.REACT_APP_MAIN_BACKEND_TOKEN}`
        },
        signal: controller.signal,
      })

      setData({
        data,
        error: false,
        isLoading: false
      })
    } catch (err) {
      console.log(err)
      if ((err as AxiosError).code === 'ERR_CANCELED') return
      setData({
        data: null,
        error: true,
        isLoading: false
      })
    }
  }, [])

  useEffect(() => {
    // If no URL, don't fetch
    if (!url) return

    const controller = new AbortController()

    if (debounce) {
      const timeout = setTimeout(() => fetchData(url, controller), debounce)

      return () => clearTimeout(timeout)
    }

    fetchData(url, controller)

    return () => controller.abort()
  }, [url])

  return { ...data, fetchData: () => fetchData(url || '', new AbortController()) }
}